import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronDown, ChevronRight, Upload, X, Loader2, FileText, AlertCircle, Plus, Eye, ZoomIn, ZoomOut, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Document, Page, pdfjs } from 'react-pdf';
import { patentApi, taskApi, documentApi } from '@/services/api';
import { useSessionStore, useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { addLoadingDocument } from '@/utils/loadingDocuments';
import type { ProsecutionDoc, SelectedDocInfo } from '@/components/TemplatesPage';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure pdfjs worker (may already be set globally, safe to re-set)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ApplicationDocSelectorProps {
  selectedAppNo: string | null;
  onAppNoChange: (appNo: string | null) => void;
  selectedOfficeAction: SelectedDocInfo | null;
  onOfficeActionChange: (doc: SelectedDocInfo | null) => void;
  selectedClaims: SelectedDocInfo | null;
  onClaimsChange: (doc: SelectedDocInfo | null) => void;
  selectedSupplementalDocs: SelectedDocInfo[];
  onSupplementalDocsChange: (docs: SelectedDocInfo[]) => void;
  uploadedClaims: SelectedDocInfo | null;
  onUploadedClaimsChange: (doc: SelectedDocInfo | null) => void;
  uploadedOA: SelectedDocInfo | null;
  onUploadedOAChange: (doc: SelectedDocInfo | null) => void;
}

export function ApplicationDocSelector({
  selectedAppNo,
  onAppNoChange,
  selectedOfficeAction,
  onOfficeActionChange,
  selectedClaims,
  onClaimsChange,
  selectedSupplementalDocs,
  onSupplementalDocsChange,
  uploadedClaims,
  onUploadedClaimsChange,
  uploadedOA,
  onUploadedOAChange,
}: ApplicationDocSelectorProps) {
  const { addNotification } = useNotificationStore();
  const sessionApplications = useSessionStore((state) => state.sessionApplications);

  const [searchQuery, setSearchQuery] = useState('');
  const [prosecutionDocs, setProsecutionDocs] = useState<ProsecutionDoc[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Categorized docs
  const [oaDocs, setOaDocs] = useState<ProsecutionDoc[]>([]);
  const [claimsDocs, setClaimsDocs] = useState<ProsecutionDoc[]>([]);
  const [supplementalDocs, setSupplementalDocs] = useState<ProsecutionDoc[]>([]);

  // Collapsible states
  const [oaOpen, setOaOpen] = useState(true);
  const [claimsOpen, setClaimsOpen] = useState(true);
  const [supplementalOpen, setSupplementalOpen] = useState(true);

  // Claims upload
  const claimsFileRef = useRef<HTMLInputElement>(null);
  const [uploadingClaims, setUploadingClaims] = useState(false);

  // OA upload
  const oaFileRef = useRef<HTMLInputElement>(null);
  const [uploadingOA, setUploadingOA] = useState(false);

  // Download state
  const [downloadingDocs, setDownloadingDocs] = useState<Set<string>>(new Set());

  // Claims PDF viewer
  const [viewingClaimsDoc, setViewingClaimsDoc] = useState<ProsecutionDoc | null>(null);
  const [claimsPdfUrl, setClaimsPdfUrl] = useState<string | null>(null);
  const [claimsPdfLoading, setClaimsPdfLoading] = useState(false);
  const [claimsPdfStatus, setClaimsPdfStatus] = useState('');
  const [claimsPdfNumPages, setClaimsPdfNumPages] = useState(0);
  const [claimsPdfScale, setClaimsPdfScale] = useState(1.2);
  const claimsPdfStopPollingRef = useRef<(() => void) | null>(null);

  // Add new application state
  const setSessionApplications = useSessionStore((state) => state.setSessionApplications);
  const startPolling = useJobPollingStore((state) => state.startPolling);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');
  const [onboardingElapsed, setOnboardingElapsed] = useState(0);
  const onboardingStartTime = useRef<number | null>(null);
  const onboardingAppId = useRef<string | null>(null);

  // Elapsed time timer for application onboarding
  useEffect(() => {
    if (isOnboarding) {
      onboardingStartTime.current = Date.now();
      setOnboardingElapsed(0);
      const interval = setInterval(() => {
        if (onboardingStartTime.current) {
          setOnboardingElapsed(Math.floor((Date.now() - onboardingStartTime.current) / 1000));
        }
      }, 1000);
      return () => clearInterval(interval);
    } else {
      onboardingStartTime.current = null;
    }
  }, [isOnboarding]);

  // Listen for job-complete events to detect when onboarding finishes
  useEffect(() => {
    const handleJobComplete = async (event: CustomEvent) => {
      const { docType, docId, success } = event.detail;
      if (docType === 'application' && docId === onboardingAppId.current) {
        setIsOnboarding(false);
        if (success) {
          try {
            const apps = await patentApi.getSessionApplications(true);
            setSessionApplications(apps);
            localStorage.setItem('session_applications', JSON.stringify(apps));
          } catch (err) {
            console.error('Failed to refresh session applications after onboarding:', err);
          }
          handleAppSelect(docId);
          setSearchQuery('');
          setOnboardingError('');
        } else {
          setOnboardingError('Failed to load application. Please verify the number and try again.');
        }
        onboardingAppId.current = null;
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);
    return () => window.removeEventListener('job-complete', handleJobComplete as EventListener);
  }, [setSessionApplications]);

  const handleAddNewApplication = async () => {
    setOnboardingError('');

    let cleanedId = searchQuery
      .trim()
      .replace(/,/g, '')
      .replace(/\//g, '')
      .replace(/^US/i, '')
      .replace(/[A-Z]\d*$/i, '');

    if (!/^\d{8}$/.test(cleanedId)) {
      setOnboardingError('Application number must be 8 digits (e.g., 16123456 or 16/123,456)');
      return;
    }

    // Check if already in session
    const alreadyLoaded = sessionApplications?.some((app: any) => {
      const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
      return appId === cleanedId;
    });
    if (alreadyLoaded) {
      handleAppSelect(cleanedId);
      setSearchQuery('');
      return;
    }

    setIsOnboarding(true);
    onboardingAppId.current = cleanedId;

    try {
      const response = await documentApi.fetchAndLoadDocument('application', cleanedId);

      if (response?.job_id) {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== 'undefined' ? JSON.parse(stored) : {};
        context.current_applications = [...(context.current_applications || []), cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addLoadingDocument(cleanedId, 'Application', cleanedId);
        startPolling(response.job_id, 'application', cleanedId);
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (error: any) {
      console.error(`Failed to onboard application ${cleanedId}:`, error);
      setIsOnboarding(false);
      onboardingAppId.current = null;
      setOnboardingError(error.message || 'Failed to load application. Please try again.');
    }
  };

  const handleDownload = async (doc: ProsecutionDoc) => {
    if (!selectedAppNo || downloadingDocs.has(doc.docId)) return;
    setDownloadingDocs((prev) => new Set([...prev, doc.docId]));
    try {
      const result = await patentApi.fetchProsecutionDoc(selectedAppNo, doc.docId, doc.rawDoc, true);
      if (result?.url) {
        window.open(result.url, '_blank');
      } else if (result?.file_content) {
        const byteCharacters = atob(result.file_content);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedAppNo}-${doc.docId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const urlResult = await patentApi.getProsecutionDocUrl(selectedAppNo, doc.docId, true);
        const url = urlResult?.url || urlResult?.download_url;
        if (url) window.open(url, '_blank');
        else throw new Error('Unable to retrieve document');
      }
    } catch (err: any) {
      addNotification({ type: 'error', title: 'Download failed', message: err.message || 'Failed to download PDF' });
    } finally {
      setDownloadingDocs((prev) => { const s = new Set(prev); s.delete(doc.docId); return s; });
    }
  };

  const fetchProsecutionDocs = useCallback(async (appNumber: string) => {
    try {
      setLoadingDocs(true);
      const result = await patentApi.fetchProsecutionDocInfo(appNumber);

      let prosInfoArray: any[] = [];
      if (result.pros_info && Array.isArray(result.pros_info)) {
        prosInfoArray = result.pros_info;
      } else if (Array.isArray(result)) {
        prosInfoArray = result;
      }

      const docs: ProsecutionDoc[] = prosInfoArray
        .filter((doc: any) => Array.isArray(doc.downloadOptionBag))
        .map((doc: any) => {
          const pdf = doc.downloadOptionBag.find((d: any) => d.mimeTypeIdentifier === 'PDF');
          return {
            date: new Date(doc.officialDate).toLocaleDateString(),
            rawDate: new Date(doc.officialDate),
            pdfUrl: pdf?.downloadUrl || null,
            description: doc.documentCodeDescriptionText || doc.documentCode,
            documentCode: doc.documentCode,
            docId: doc.documentIdentifier,
            rawDoc: doc,
          };
        })
        .filter((doc: ProsecutionDoc) => !!doc.pdfUrl)
        .sort((a: ProsecutionDoc, b: ProsecutionDoc) => b.rawDate.getTime() - a.rawDate.getTime());

      setProsecutionDocs(docs);

      // Filter office actions
      const oaDocuments = docs.filter((doc) => {
        const desc = doc.description.toLowerCase();
        const code = doc.documentCode;
        if (code && code.startsWith('A.')) return false;
        return code.includes('CT') || /(office action|rejection|allowance|notification)/i.test(desc);
      });
      setOaDocs(oaDocuments);

      // Filter claims
      const claimsDocuments = docs.filter((doc) => {
        const desc = doc.description.toLowerCase();
        return desc.includes('claims') || doc.documentCode === 'CLM';
      });
      setClaimsDocs(claimsDocuments);

      // Filter supplemental (advisory actions, interview summaries)
      const supplementalDocuments = docs.filter((doc) => {
        const desc = doc.description.toLowerCase();
        return doc.documentCode === 'AA' || doc.documentCode === 'ISUM' || /(advisory|interview)/i.test(desc);
      });
      setSupplementalDocs(supplementalDocuments);
    } catch (error) {
      console.error('Error fetching prosecution docs:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to load prosecution documents.' });
    } finally {
      setLoadingDocs(false);
    }
  }, [addNotification]);

  useEffect(() => {
    if (selectedAppNo) {
      fetchProsecutionDocs(selectedAppNo);
    } else {
      setProsecutionDocs([]);
      setOaDocs([]);
      setClaimsDocs([]);
      setSupplementalDocs([]);
    }
  }, [selectedAppNo, fetchProsecutionDocs]);

  const handleAppSelect = (appNo: string) => {
    // Clear selections when changing app
    onOfficeActionChange(null);
    onClaimsChange(null);
    onSupplementalDocsChange([]);
    onUploadedClaimsChange(null);
    onAppNoChange(appNo);
  };

  const handleOASelect = (doc: ProsecutionDoc) => {
    if (selectedOfficeAction?.docId === doc.docId) {
      onOfficeActionChange(null);
    } else {
      onOfficeActionChange({
        docId: doc.docId,
        appNo: selectedAppNo!,
        description: doc.description,
        date: doc.date,
        type: 'office_action',
      });
    }
  };

  const handleClaimsSelect = (doc: ProsecutionDoc) => {
    if (uploadedClaims) return; // Disable prosecution claims when uploaded claims exist
    if (selectedClaims?.docId === doc.docId) {
      onClaimsChange(null);
    } else {
      onClaimsChange({
        docId: doc.docId,
        appNo: selectedAppNo!,
        description: doc.description,
        date: doc.date,
        type: 'claims',
      });
    }
  };

  const handleSupplementalToggle = (doc: ProsecutionDoc) => {
    const existing = selectedSupplementalDocs.find((d) => d.docId === doc.docId);
    if (existing) {
      onSupplementalDocsChange(selectedSupplementalDocs.filter((d) => d.docId !== doc.docId));
    } else {
      onSupplementalDocsChange([
        ...selectedSupplementalDocs,
        {
          docId: doc.docId,
          appNo: selectedAppNo!,
          description: doc.description,
          date: doc.date,
          type: 'supplemental',
        },
      ]);
    }
  };

  const handleOAUpload = async () => {
    const file = oaFileRef.current?.files?.[0];
    if (!file) return;
    try {
      setUploadingOA(true);
      const response = await taskApi.uploadOADocument(file);
      if (response?.job_id) {
        taskApi.startGraduatedPolling(
          async () => {
            const result = await documentApi.getJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              onUploadedOAChange({
                docId: response.upload_id || '',
                appNo: '',
                description: file.name,
                date: new Date().toLocaleDateString(),
                type: 'uploaded_oa',
                filename: file.name,
                uploadId: response.upload_id || '',
              });
              addNotification({ type: 'success', title: 'OA Uploaded', message: 'Office action document uploaded.' });
            } else {
              addNotification({ type: 'error', title: 'Error', message: 'OA upload failed.' });
            }
            setUploadingOA(false);
            if (oaFileRef.current) oaFileRef.current.value = '';
          },
          () => {
            addNotification({ type: 'error', title: 'Error', message: 'OA upload failed.' });
            setUploadingOA(false);
          }
        );
      } else {
        addNotification({ type: 'error', title: 'Error', message: 'Failed to upload OA.' });
        setUploadingOA(false);
      }
    } catch (error) {
      console.error('OA upload error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to upload office action.' });
      setUploadingOA(false);
    }
  };

  const handleClaimsUpload = async () => {
    const file = claimsFileRef.current?.files?.[0];
    if (!file) return;

    try {
      setUploadingClaims(true);
      const response = await taskApi.uploadClaimsDocument(file, selectedAppNo);

      if (response?.job_id) {
        taskApi.startGraduatedPolling(
          async () => {
            const result = await documentApi.getJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              onClaimsChange(null); // Clear prosecution claims
              onUploadedClaimsChange({
                docId: result.doc_id || response.doc_id || '',
                appNo: selectedAppNo || '',
                description: file.name,
                date: new Date().toLocaleDateString(),
                type: 'uploaded_claims',
                filename: file.name,
                uploadId: result.upload_id || response.upload_id || '',
              });
              addNotification({ type: 'success', title: 'Claims Uploaded', message: 'Custom claims document uploaded.' });
            } else {
              addNotification({ type: 'error', title: 'Error', message: 'Claims upload failed.' });
            }
            setUploadingClaims(false);
            if (claimsFileRef.current) claimsFileRef.current.value = '';
          },
          () => {
            addNotification({ type: 'error', title: 'Error', message: 'Claims upload failed.' });
            setUploadingClaims(false);
          }
        );
      } else if (response?.upload_id || response?.doc_id) {
        // Immediate response without polling
        onClaimsChange(null);
        onUploadedClaimsChange({
          docId: response.doc_id || '',
          appNo: selectedAppNo || '',
          description: file.name,
          date: new Date().toLocaleDateString(),
          type: 'uploaded_claims',
          filename: file.name,
          uploadId: response.upload_id || '',
        });
        addNotification({ type: 'success', title: 'Claims Uploaded', message: 'Custom claims document uploaded.' });
        setUploadingClaims(false);
        if (claimsFileRef.current) claimsFileRef.current.value = '';
      } else {
        addNotification({ type: 'error', title: 'Error', message: 'Failed to upload claims.' });
        setUploadingClaims(false);
      }
    } catch (error) {
      console.error('Claims upload error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to upload claims document.' });
      setUploadingClaims(false);
    }
  };

  // Claims PDF viewer handlers
  const handleViewClaimsPdf = useCallback(async (doc: ProsecutionDoc) => {
    setViewingClaimsDoc(doc);
    setClaimsPdfUrl(null);
    setClaimsPdfLoading(true);
    setClaimsPdfStatus('Requesting PDF...');
    setClaimsPdfNumPages(0);

    if (claimsPdfStopPollingRef.current) {
      claimsPdfStopPollingRef.current();
      claimsPdfStopPollingRef.current = null;
    }

    try {
      const response = await patentApi.getProsecutionDocPdf(selectedAppNo!, doc.docId);

      if (response?.job_id) {
        setClaimsPdfStatus('Generating PDF...');

        claimsPdfStopPollingRef.current = taskApi.startGraduatedPolling(
          async () => {
            const result = await patentApi.getPdfJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          async (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              try {
                setClaimsPdfStatus('Downloading PDF...');
                const url = await patentApi.downloadProsecutionDocPdf(selectedAppNo!, doc.docId);
                setClaimsPdfUrl(url);
                setClaimsPdfLoading(false);
                setClaimsPdfStatus('');
              } catch (error) {
                console.error('Error downloading claims PDF:', error);
                setClaimsPdfLoading(false);
                setClaimsPdfStatus('');
                addNotification({ type: 'error', title: 'Error', message: 'Failed to download claims PDF.' });
              }
            } else {
              setClaimsPdfLoading(false);
              setClaimsPdfStatus('');
              addNotification({ type: 'error', title: 'Error', message: `Failed to load PDF: ${result?.status || 'Unknown error'}` });
            }
          },
          (error) => {
            console.error('Claims PDF polling error:', error);
            setClaimsPdfLoading(false);
            setClaimsPdfStatus('');
            addNotification({ type: 'error', title: 'Error', message: 'Failed to load claims PDF.' });
          },
          (_attempts, elapsedTime) => {
            setClaimsPdfStatus(`Loading PDF... (${Math.floor(elapsedTime / 1000)}s)`);
          }
        );
      } else {
        setClaimsPdfLoading(false);
        setClaimsPdfStatus('');
        addNotification({ type: 'error', title: 'Error', message: 'Failed to start PDF generation.' });
      }
    } catch (error) {
      console.error('Error loading claims PDF:', error);
      setClaimsPdfLoading(false);
      setClaimsPdfStatus('');
      addNotification({ type: 'error', title: 'Error', message: 'Failed to load claims PDF.' });
    }
  }, [selectedAppNo, addNotification]);

  const handleCloseClaimsPdf = useCallback(() => {
    if (claimsPdfStopPollingRef.current) {
      claimsPdfStopPollingRef.current();
      claimsPdfStopPollingRef.current = null;
    }
    setViewingClaimsDoc(null);
    setClaimsPdfUrl(null);
    setClaimsPdfLoading(false);
    setClaimsPdfStatus('');
  }, []);

  const filteredApps = sessionApplications.filter((app: any) => {
    const appData = app.applicationMetaData || app.application?.applicationMetaData;
    const title = appData?.inventionTitle || '';
    // Exclude applications without a title
    if (!title || title.trim() === '') return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText || '';
    const patentNumber = appData?.patentNumber || '';
    return appId.toLowerCase().includes(q) || title.toLowerCase().includes(q) || patentNumber.toLowerCase().includes(q);
  });

  // Count total selections
  const totalSelections = [selectedOfficeAction, selectedClaims, uploadedClaims].filter(Boolean).length + selectedSupplementalDocs.length;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-200 bg-white/60 sticky top-0 z-10">
        <h2 className="text-sm font-semibold text-slate-700">Application & Documents</h2>
      </div>

      <div className="p-2">
          {/* Upload OA — always visible, no app needed */}
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-600 mb-1.5">Upload Office Action</div>
            {uploadedOA ? (
              <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-orange-50 border-2 border-dashed border-orange-300 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-orange-800">Uploaded OA</div>
                  <div className="truncate text-slate-600">{uploadedOA.filename}</div>
                </div>
                <button className="text-orange-500 hover:text-orange-700" onClick={() => onUploadedOAChange(null)} title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div>
                <input ref={oaFileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={handleOAUpload} disabled={uploadingOA} />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1 text-xs h-7 border-orange-200 text-orange-700 hover:bg-orange-50"
                  onClick={() => oaFileRef.current?.click()}
                  disabled={uploadingOA}
                >
                  {uploadingOA ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {uploadingOA ? 'Uploading...' : 'Upload OA PDF'}
                </Button>
                <p className="text-[10px] text-slate-400 mt-1">Use without selecting an application</p>
              </div>
            )}
          </div>

          {/* Upload Claims — always visible, no app needed */}
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-600 mb-1.5">Upload Claims</div>
            {uploadedClaims ? (
              <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-purple-50 border-2 border-dashed border-purple-300 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-purple-800">Uploaded Claims</div>
                  <div className="truncate text-slate-600">{uploadedClaims.filename}</div>
                </div>
                <button className="text-purple-500 hover:text-purple-700" onClick={() => onUploadedClaimsChange(null)} title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div>
                <input ref={claimsFileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleClaimsUpload} disabled={uploadingClaims} />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1 text-xs h-7 border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={() => claimsFileRef.current?.click()}
                  disabled={uploadingClaims}
                >
                  {uploadingClaims ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {uploadingClaims ? 'Uploading...' : 'Upload Claims'}
                </Button>
                <p className="text-[10px] text-slate-400 mt-1">Use without selecting an application</p>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 mb-2" />

          {/* App Search + Add */}
          <div className="flex gap-1.5 mb-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search or enter app number..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setOnboardingError(''); }}
                className="pl-7 h-8 text-xs"
                disabled={isOnboarding}
              />
            </div>
            <Button
              onClick={handleAddNewApplication}
              disabled={isOnboarding || !searchQuery.trim()}
              variant="outline"
              size="sm"
              className="h-8 px-2 flex-shrink-0"
              title="Add new application"
            >
              {isOnboarding ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>

          {/* Onboarding status */}
          {isOnboarding && (
            <div className="flex items-center gap-1.5 mb-2 px-1 text-[11px] text-blue-600">
              <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
              <span>Loading application... {Math.floor(onboardingElapsed / 60)}:{String(onboardingElapsed % 60).padStart(2, '0')}</span>
            </div>
          )}
          {onboardingError && (
            <p className="text-[11px] text-red-600 mb-2 px-1">{onboardingError}</p>
          )}

          {/* App List */}
          {!selectedAppNo ? (
            <div className="space-y-0.5">
              {filteredApps.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-xs text-slate-400 mb-2">
                    {!searchQuery ? 'No applications in session.' : 'No matching applications.'}
                  </p>
                  {searchQuery.trim() && !isOnboarding && (
                    <Button
                      onClick={handleAddNewApplication}
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 gap-1 max-w-full"
                    >
                      <Plus className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">Add "{searchQuery.trim()}" as new application</span>
                    </Button>
                  )}
                </div>
              ) : (
                filteredApps.map((app: any, index: number) => {
                  const appData = app.applicationMetaData || app.application?.applicationMetaData;
                  const appNo = app.applicationNumberText || app.application_id || app.application?.applicationNumberText || '';
                  const title = appData?.inventionTitle || '';
                  return (
                    <div
                      key={appNo || `app-${index}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-slate-50 text-xs"
                      onClick={() => handleAppSelect(appNo)}
                    >
                      <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-700 truncate">{appNo}</div>
                        {title && <div className="text-slate-400 truncate">{title}</div>}
                      </div>
                      <ChevronRight className="w-3 h-3 text-slate-300" />
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div>
              {/* Selected App Header */}
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="gap-1 text-xs bg-teal-50 border-teal-200 text-teal-700">
                  {selectedAppNo}
                  <button onClick={() => { onAppNoChange(null); onOfficeActionChange(null); onClaimsChange(null); onSupplementalDocsChange([]); onUploadedClaimsChange(null); }}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              </div>

              {loadingDocs ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  <span className="ml-2 text-xs text-slate-400">Loading documents...</span>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Office Actions */}
                  <Collapsible open={oaOpen} onOpenChange={setOaOpen}>
                    <CollapsibleTrigger className="flex items-center gap-1 w-full text-xs font-medium text-slate-600 hover:text-slate-800 py-1">
                      {oaOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Office Actions ({oaDocs.length})
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-0.5 ml-2">
                        {oaDocs.length === 0 ? (
                          <p className="text-xs text-slate-400 py-1">No office actions found.</p>
                        ) : (
                          oaDocs.map((doc) => {
                            const isSelected = selectedOfficeAction?.docId === doc.docId;
                            return (
                              <div
                                key={doc.docId}
                                className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs transition-colors ${
                                  isSelected ? 'bg-orange-50 border border-orange-200' : 'hover:bg-slate-50 border border-transparent'
                                }`}
                                onClick={() => handleOASelect(doc)}
                              >
                                <div className={`w-2 h-2 rounded-full border flex-shrink-0 ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-300'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="truncate text-slate-700">{doc.description}</div>
                                  <div className="text-slate-400">{doc.date}</div>
                                </div>
                                <button
                                  className="p-0.5 rounded hover:bg-orange-100 text-slate-400 hover:text-orange-700 flex-shrink-0"
                                  title="Download PDF"
                                  onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                                  disabled={downloadingDocs.has(doc.docId)}
                                >
                                  {downloadingDocs.has(doc.docId) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Claims */}
                  <Collapsible open={claimsOpen} onOpenChange={setClaimsOpen}>
                    <CollapsibleTrigger className="flex items-center gap-1 w-full text-xs font-medium text-slate-600 hover:text-slate-800 py-1">
                      {claimsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Claims ({claimsDocs.length})
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-0.5 ml-2">
                        {/* Uploaded claims indicator */}
                        {uploadedClaims && (
                          <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-purple-50 border-2 border-dashed border-purple-300 text-xs mb-1">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-purple-800">Uploaded Claims</div>
                              <div className="truncate text-slate-600">{uploadedClaims.filename}</div>
                            </div>
                            <button className="text-purple-500 hover:text-purple-700" onClick={() => onUploadedClaimsChange(null)} title="Remove">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}

                        {claimsDocs.length === 0 && !uploadedClaims ? (
                          <p className="text-xs text-slate-400 py-1">No claims found.</p>
                        ) : (
                          claimsDocs.map((doc) => {
                            const isSelected = selectedClaims?.docId === doc.docId;
                            const isDisabled = !!uploadedClaims;
                            return (
                              <div
                                key={doc.docId}
                                className={`flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors ${
                                  isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                } ${isSelected ? 'bg-green-50 border border-green-200' : 'hover:bg-slate-50 border border-transparent'}`}
                                onClick={() => !isDisabled && handleClaimsSelect(doc)}
                              >
                                <div className={`w-2 h-2 rounded-full border ${isSelected ? 'bg-green-500 border-green-500' : 'border-slate-300'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="truncate text-slate-700">{doc.description}</div>
                                  <div className="text-slate-400">{doc.date}</div>
                                </div>
                                <button
                                  className="p-0.5 rounded hover:bg-green-100 text-slate-400 hover:text-green-700 flex-shrink-0"
                                  title="View PDF"
                                  onClick={(e) => { e.stopPropagation(); handleViewClaimsPdf(doc); }}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className="p-0.5 rounded hover:bg-green-100 text-slate-400 hover:text-green-700 flex-shrink-0"
                                  title="Download PDF"
                                  onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                                  disabled={downloadingDocs.has(doc.docId)}
                                >
                                  {downloadingDocs.has(doc.docId) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                </button>
                              </div>
                            );
                          })
                        )}

                        {/* Upload Claims */}
                        <div className="pt-1">
                          <input ref={claimsFileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleClaimsUpload} disabled={uploadingClaims} />
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full gap-1 text-xs h-7"
                            onClick={() => claimsFileRef.current?.click()}
                            disabled={uploadingClaims}
                          >
                            {uploadingClaims ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {uploadingClaims ? 'Uploading...' : 'Upload Claims'}
                          </Button>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Supplemental */}
                  <Collapsible open={supplementalOpen} onOpenChange={setSupplementalOpen}>
                    <CollapsibleTrigger className="flex items-center gap-1 w-full text-xs font-medium text-slate-600 hover:text-slate-800 py-1">
                      {supplementalOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Supplemental ({supplementalDocs.length})
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-0.5 ml-2">
                        {supplementalDocs.length === 0 ? (
                          <p className="text-xs text-slate-400 py-1">No supplemental documents found.</p>
                        ) : (
                          supplementalDocs.map((doc) => {
                            const isSelected = selectedSupplementalDocs.some((d) => d.docId === doc.docId);
                            return (
                              <div
                                key={doc.docId}
                                className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs transition-colors ${
                                  isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
                                }`}
                                onClick={() => handleSupplementalToggle(doc)}
                              >
                                <div className={`w-3 h-3 rounded-sm border flex items-center justify-center ${
                                  isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300'
                                }`}>
                                  {isSelected && <span className="text-[8px]">&#10003;</span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="truncate text-slate-700">{doc.description}</div>
                                  <div className="text-slate-400">{doc.date}</div>
                                </div>
                                <button
                                  className="p-0.5 rounded hover:bg-blue-100 text-slate-400 hover:text-blue-700 flex-shrink-0"
                                  title="Download PDF"
                                  onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                                  disabled={downloadingDocs.has(doc.docId)}
                                >
                                  {downloadingDocs.has(doc.docId) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}

              {/* Selected Summary */}
              {totalSelections > 0 && (
                <div className="mt-3 pt-2 border-t border-slate-100 overflow-hidden">
                  <div className="text-xs font-medium text-slate-500 mb-1">Selected Documents</div>
                  <div className="flex flex-wrap gap-1 overflow-hidden">
                    {selectedOfficeAction && (
                      <Badge variant="outline" className="text-[10px] gap-1 bg-orange-50 border-orange-200 text-orange-700">
                        OA: {selectedOfficeAction.description.slice(0, 20)}
                        <button onClick={() => onOfficeActionChange(null)}><X className="w-2.5 h-2.5" /></button>
                      </Badge>
                    )}
                    {selectedClaims && (
                      <Badge variant="outline" className="text-[10px] gap-1 bg-green-50 border-green-200 text-green-700">
                        Claims: {selectedClaims.description.slice(0, 20)}
                        <button onClick={() => onClaimsChange(null)}><X className="w-2.5 h-2.5" /></button>
                      </Badge>
                    )}
                    {uploadedClaims && (
                      <Badge variant="outline" className="text-[10px] gap-1 bg-purple-50 border-purple-200 text-purple-700">
                        Uploaded: {uploadedClaims.filename?.slice(0, 20)}
                        <button onClick={() => onUploadedClaimsChange(null)}><X className="w-2.5 h-2.5" /></button>
                      </Badge>
                    )}
                    {selectedSupplementalDocs.map((doc) => (
                      <Badge key={doc.docId} variant="outline" className="text-[10px] gap-1 bg-blue-50 border-blue-200 text-blue-700">
                        {doc.description.slice(0, 20)}
                        <button onClick={() => onSupplementalDocsChange(selectedSupplementalDocs.filter((d) => d.docId !== doc.docId))}>
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
      </div>

      {/* Fullscreen Claims PDF Viewer Dialog */}
      <Dialog open={!!viewingClaimsDoc} onOpenChange={(open) => { if (!open) handleCloseClaimsPdf(); }}>
        <DialogContent className="!max-w-[95vw] w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-4 py-2 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="text-sm font-medium text-slate-700 truncate">
                {viewingClaimsDoc?.description} ({viewingClaimsDoc?.date})
              </DialogTitle>
              {claimsPdfUrl && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setClaimsPdfScale((s) => Math.max(0.5, s - 0.1))}>
                    <ZoomOut className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-xs text-slate-500 w-12 text-center">{Math.round(claimsPdfScale * 100)}%</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setClaimsPdfScale((s) => Math.min(3, s + 0.1))}>
                    <ZoomIn className="w-3.5 h-3.5" />
                  </Button>
                  {claimsPdfNumPages > 0 && (
                    <>
                      <div className="mx-1 h-4 w-px bg-slate-200" />
                      <span className="text-xs text-slate-500">{claimsPdfNumPages} pages</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-auto bg-slate-100">
            {claimsPdfLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                <p className="mt-3 text-sm text-slate-500">{claimsPdfStatus || 'Loading...'}</p>
              </div>
            ) : claimsPdfUrl ? (
              <div className="flex flex-col items-center p-4 gap-2">
                <Document
                  file={claimsPdfUrl}
                  onLoadSuccess={({ numPages: pages }) => setClaimsPdfNumPages(pages)}
                  loading={
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  }
                >
                  {Array.from({ length: claimsPdfNumPages }, (_, i) => (
                    <div key={i} className="mb-2 shadow-sm">
                      <Page
                        pageNumber={i + 1}
                        scale={claimsPdfScale}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </div>
                  ))}
                </Document>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <FileText className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">PDF not available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
